import { Formik } from 'formik';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { PhoneNumber, ToastError, ToastSuccess } from 'utils';
import { capitalizeName } from 'utils/capitalize';
import { ContactFormFields } from './contact-form-fields-type';
import { ContactFormModalProps } from './contact-form-modal';
import contactFormValidationSchema from './contact-form-validation-schema';
import ContactFormView from './contact-form-view';
import {
  CreateContactData,
  CreateContactRequest,
  CreatedResponse,
} from 'generated/api-client';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Options } from 'generated/api-client/client';

type ContactFormCreateContainerProps = ContactFormModalProps & {
  createContact: UseMutateAsyncFunction<
    CreatedResponse,
    AxiosError<Error, any>,
    Options<CreateContactData>,
    unknown
  >;
  isExistingContact: (
    givenName: string,
    familyName: string,
    email: string,
    phoneNumber: string,
  ) => Promise<boolean>;
};

const ContactFormCreateContainer: FunctionComponent<
  ContactFormCreateContainerProps
> = ({ createContact, isExistingContact, onClose, ...rest }) => {
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const onSubmit = useCallback(async (values: ContactFormFields) => {
    // Check for duplicate contact (same givenName/familyName/account.email)
    if (
      await isExistingContact(
        values.givenName,
        values.familyName,
        values.email,
        values.phoneNumber,
      )
    ) {
      setIsDuplicate(true);
      return;
    }

    await createContact({
      body: {
        givenName: capitalizeName(values.givenName).trim(),
        familyName: capitalizeName(values.familyName.trim()),
        phoneNumber: values.phoneNumber
          ? PhoneNumber.toE164(values.phoneNumber)
          : null,
        email: values.email.toLowerCase().trim() || null,
      },
    }).catch((error) => {
      ToastError('Erreur', `Impossible de créer le contact`);
      throw error;
    });

    if (onClose) {
      onClose();
    }

    ToastSuccess('Succès', `Contact créé`);
  }, []);

  const initialValues: ContactFormFields = useMemo(() => {
    return {
      givenName: '',
      familyName: '',
      phoneNumber: '',
      email: '',
    };
  }, []);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={contactFormValidationSchema}
      validateOnChange={false}
    >
      <ContactFormView onClose={onClose} isDuplicate={isDuplicate} {...rest} />
    </Formik>
  );
};

export default ContactFormCreateContainer;
